<template>
  <div v-if="visible" class="subtitle-style-modal-backdrop" @click.self="$emit('close')">
    <section class="subtitle-style-modal" role="dialog" aria-modal="true">
      <header class="subtitle-style-modal-header">
        <div>
          <h2>{{ tt("userProfile.subtitleStyleModalTitle", "Estilo de subtítulos") }}</h2>
          <p>{{ tt("userProfile.subtitleStyleModalHint", "Se aplicará a clips nuevos.") }}</p>
        </div>
        <button type="button" class="subtitle-style-close" @click="$emit('close')">
          <i class="ti-close" aria-hidden="true"></i>
        </button>
      </header>

      <div class="subtitle-style-field">
        <span>{{ tt("userProfile.subtitleStyleFont", "Fuente") }}</span>
        <div class="subtitle-style-font-grid">
          <button
            v-for="font in subtitleFonts"
            :key="font"
            type="button"
            class="subtitle-style-font-option"
            :class="{ 'is-selected': draft.font === font }"
            :aria-pressed="draft.font === font"
            @click="draft.font = font"
          >
            <span class="subtitle-style-font-sample" :style="subtitleFontPreviewStyle(font)">CLIPR</span>
            <span class="subtitle-style-font-name">{{ font }}</span>
          </button>
        </div>
      </div>

      <div class="subtitle-style-field">
        <span>{{ tt("userProfile.subtitleStyleEffect", "Tipo de resaltado") }}</span>
        <div class="subtitle-style-effect-grid">
          <button
            type="button"
            class="subtitle-style-effect-option"
            :class="{ 'is-selected': draft.activeEffect === 'text_color' }"
            :aria-pressed="draft.activeEffect === 'text_color'"
            @click="draft.activeEffect = 'text_color'"
          >
            <span class="subtitle-style-effect-text-color" :style="{ color: draft.activeColor }">CLIPR</span>
            <span>{{ tt("userProfile.subtitleStyleEffectTextColor", "Color en texto") }}</span>
          </button>
          <button
            type="button"
            class="subtitle-style-effect-option"
            :class="{ 'is-selected': draft.activeEffect === 'background' }"
            :aria-pressed="draft.activeEffect === 'background'"
            @click="draft.activeEffect = 'background'"
          >
            <span class="subtitle-style-effect-background" :style="{ '--subtitle-effect-color': draft.activeColor }">CLIPR</span>
            <span>{{ tt("userProfile.subtitleStyleEffectBackground", "Resaltado") }}</span>
          </button>
        </div>
      </div>

      <label class="subtitle-style-field">
        <span>{{ tt("userProfile.subtitleStyleColor", "Color") }}</span>
        <div class="subtitle-style-color-control">
          <input v-model="draft.activeColor" type="color" class="subtitle-style-color-picker">
          <span>{{ draft.activeColor }}</span>
        </div>
      </label>

      <div class="subtitle-style-preview" :style="subtitlePreviewStyle">
        <span
          v-for="(word, index) in subtitlePreviewWords"
          :key="word"
          class="subtitle-style-preview-word"
          :class="{
            'is-active': subtitlePreviewActiveIndex === index,
            [`is-${draft.activeEffect}`]: subtitlePreviewActiveIndex === index,
          }"
          :data-word="word"
        ><span class="subtitle-style-preview-word-text">{{ word }}</span></span>
      </div>

      <footer class="subtitle-style-modal-actions">
        <button type="button" class="subtitle-style-cancel" :disabled="saving" @click="$emit('close')">
          {{ tt("common.cancel", "Cancelar") }}
        </button>
        <button type="button" class="btn btn-primary" :disabled="saving" @click="save">
          {{ saving ? tt("common.saving", "Guardando...") : tt("userProfile.savePreferences", "Guardar") }}
        </button>
      </footer>
    </section>
  </div>
</template>

<script>
export default {
  props: {
    visible: Boolean,
    saving: Boolean,
    value: {
      type: Object,
      required: true,
    },
  },
  emits: ["close", "save"],
  data() {
    return {
      draft: {
        ...this.value,
        activeEffect: this.value.activeEffect || "text_color",
      },
      subtitlePreviewActiveIndex: 0,
      subtitlePreviewTimer: null,
    };
  },
  computed: {
    subtitleFonts() {
      return ["DejaVu Sans", "DejaVu Sans Condensed", "Liberation Sans", "Anton", "Archivo Black", "Bebas Neue", "Bricolage Grotesque", "League Spartan", "Oswald"];
    },
    subtitleFontFamilies() {
      return {
        "DejaVu Sans": "Clipr Subtitle DejaVu Sans",
        "DejaVu Sans Condensed": "Clipr Subtitle DejaVu Sans Condensed",
        "Liberation Sans": "Clipr Subtitle Liberation Sans",
        Anton: "Anton",
        "Archivo Black": "Archivo Black",
        "Bebas Neue": "Bebas Neue",
        "Bricolage Grotesque": "Bricolage Grotesque",
        "League Spartan": "League Spartan",
        Oswald: "Oswald",
      };
    },
    subtitleFontScales() {
      return { "DejaVu Sans": 1, "DejaVu Sans Condensed": 1, "Liberation Sans": 1, Anton: 1.04, "Archivo Black": 1, "Bebas Neue": 1.09, "Bricolage Grotesque": 1.04, "League Spartan": 1.04, Oswald: 1.09 };
    },
    subtitlePreviewStyle() {
      return {
        fontFamily: this.subtitleFontFamilies[this.draft.font],
        "--subtitle-preview-active-color": this.draft.activeColor,
        "--subtitle-preview-font-scale": this.subtitleFontScales[this.draft.font],
      };
    },
    subtitlePreviewWords() {
      return ["CLIPR", "IS", "THE", "BEST"];
    },
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.draft = {
          ...value,
          activeEffect: value.activeEffect || "text_color",
        };
      },
    },
  },
  methods: {
    tt(key, fallback) {
      return typeof this.$t === "function" ? this.$t(key) : fallback;
    },
    subtitleFontPreviewStyle(font) {
      return {
        fontFamily: this.subtitleFontFamilies[font],
        transform: `scale(${this.subtitleFontScales[font]})`,
      };
    },
    save() {
      this.$emit("save", {
        font: this.draft.font,
        activeColor: this.draft.activeColor.toUpperCase(),
        activeEffect: this.draft.activeEffect,
      });
    },
  },
  mounted() {
    this.subtitlePreviewTimer = window.setInterval(() => {
      this.subtitlePreviewActiveIndex = (this.subtitlePreviewActiveIndex + 1) % this.subtitlePreviewWords.length;
    }, 550);
  },
  beforeUnmount() {
    window.clearInterval(this.subtitlePreviewTimer);
  },
};
</script>

<style scoped>
.subtitle-style-modal-backdrop { position: fixed; z-index: 1200; inset: 0; display: grid; place-items: center; padding: 1rem; background: rgba(3, 3, 8, .72); }
.subtitle-style-modal { width: min(480px, 100%); max-height: calc(100dvh - 2rem); overflow-y: auto; border: 1px solid rgba(192,132,252,.25); border-radius: 16px; background: #15121f; box-shadow: 0 24px 64px rgba(0,0,0,.5); padding: 1.2rem; }
.subtitle-style-modal-header { display: flex; justify-content: space-between; gap: 1rem; margin-bottom: 1.15rem; }
.subtitle-style-modal-header h2 { margin: 0; color: var(--clipr-text-primary); font-size: 1rem; font-weight: 650; }
.subtitle-style-modal-header p { margin: .28rem 0 0; color: var(--clipr-text-secondary); font-size: .78rem; }
.subtitle-style-close { width: 30px; height: 30px; border: 0; border-radius: 8px; background: transparent; color: var(--clipr-text-secondary); cursor: pointer; }
.subtitle-style-field { display: grid; gap: .4rem; margin-bottom: .95rem; color: var(--clipr-text-secondary); font-size: .76rem; font-weight: 600; }
.subtitle-style-font-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: .55rem; }
.subtitle-style-font-option { display: grid; gap: .38rem; min-width: 0; padding: .5rem; border: 1px solid rgba(148,163,184,.2); border-radius: 10px; background: #211b2e; color: #f8fafc; cursor: pointer; text-align: center; }
.subtitle-style-font-option.is-selected { border-color: #c084fc; background: linear-gradient(145deg, rgba(168,85,247,.26), rgba(217,70,239,.14)); box-shadow: 0 0 0 1px rgba(192,132,252,.32); }
.subtitle-style-font-sample { display: grid; min-height: 52px; place-items: center; border-radius: 7px; background: #08070c; color: #fff; font-size: 1.5rem; font-weight: 700; line-height: 1; paint-order: stroke fill; -webkit-text-stroke: 2px #000; text-shadow: .25px .25px 0 #000; }
.subtitle-style-font-name { overflow: hidden; color: var(--clipr-text-secondary); font-size: .64rem; font-weight: 600; line-height: 1.2; text-overflow: ellipsis; white-space: nowrap; }
.subtitle-style-effect-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .55rem; }
.subtitle-style-effect-option { display: grid; grid-template-columns: auto 1fr; align-items: center; gap: .65rem; min-width: 0; padding: .65rem .75rem; border: 1px solid rgba(148,163,184,.2); border-radius: 10px; background: #211b2e; color: #f8fafc; cursor: pointer; font-size: .72rem; font-weight: 650; text-align: left; }
.subtitle-style-effect-option.is-selected { border-color: #c084fc; background: linear-gradient(145deg, rgba(168,85,247,.26), rgba(217,70,239,.14)); box-shadow: 0 0 0 1px rgba(192,132,252,.32); }
.subtitle-style-effect-text-color, .subtitle-style-effect-background { font-family: "Clipr Subtitle DejaVu Sans", sans-serif; font-size: 1rem; font-weight: 700; line-height: 1; paint-order: stroke fill; -webkit-text-stroke: 1.5px #000; }
.subtitle-style-effect-background { color: #fff; -webkit-text-stroke: 4px var(--subtitle-effect-color); text-shadow: 0 0 0 #000; }
.subtitle-style-color-control { display: flex; align-items: center; gap: .65rem; min-height: 40px; padding: .35rem .5rem; border: 1px solid rgba(192,132,252,.35); border-radius: 9px; background: linear-gradient(135deg, rgba(168,85,247,.14), #211b2e 52%); color: #f8fafc; font-family: monospace; font-size: .86rem; letter-spacing: .04em; }
.subtitle-style-color-picker { width: 30px; height: 30px; padding: 0; border: 0; border-radius: 50%; overflow: hidden; background: transparent; cursor: pointer; }
.subtitle-style-color-picker::-webkit-color-swatch-wrapper { padding: 0; }
.subtitle-style-color-picker::-webkit-color-swatch, .subtitle-style-color-picker::-moz-color-swatch { border: 0; border-radius: 50%; }
.subtitle-style-preview { display: flex; justify-content: center; flex-wrap: wrap; gap: .35em; min-height: 112px; align-items: center; border: 1px solid rgba(255,255,255,.1); border-radius: 12px; background: #08070c; color: #fff; font-size: clamp(1.35rem, 7vw, 2rem); font-weight: 700; line-height: 1.15; padding: 1.25rem; text-align: center; text-transform: uppercase; paint-order: stroke fill; -webkit-text-stroke: 2.7px #000; text-shadow: .35px .35px 0 #000; }
.subtitle-style-preview-word { position: relative; display: inline-block; color: #fff; transform: scale(var(--subtitle-preview-font-scale)); }
.subtitle-style-preview-word-text { position: relative; z-index: 1; }
.subtitle-style-preview-word.is-active { margin-inline: .12em; }
.subtitle-style-preview-word.is-active.is-text_color { color: var(--subtitle-preview-active-color); transform: scale(calc(var(--subtitle-preview-font-scale) * 1.2)); }
.subtitle-style-preview-word.is-active.is-background::before { position: absolute; inset: 0; z-index: 0; color: var(--subtitle-preview-active-color); content: attr(data-word); paint-order: stroke fill; -webkit-text-stroke: 6.7px var(--subtitle-preview-active-color); text-shadow: none; }
.subtitle-style-modal-actions { display: flex; justify-content: flex-end; gap: .65rem; margin-top: 1.1rem; }
.subtitle-style-cancel { border: 1px solid rgba(192,132,252,.32); border-radius: 9px; background: rgba(168,85,247,.11); color: #e9d5ff; padding: .38rem .68rem; font-size: .75rem; font-weight: 600; cursor: pointer; }
@font-face { font-family: "Clipr Subtitle DejaVu Sans"; src: url("../../assets/fonts/subtitle-preview/DejaVuSans-Bold.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Clipr Subtitle DejaVu Sans Condensed"; src: url("../../assets/fonts/subtitle-preview/DejaVuSansCondensed-Bold.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Clipr Subtitle Liberation Sans"; src: url("../../assets/fonts/subtitle-preview/LiberationSans-Bold.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Anton"; src: url("../../assets/fonts/subtitle-preview/Anton-Regular.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Archivo Black"; src: url("../../assets/fonts/subtitle-preview/ArchivoBlack-Regular.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Bebas Neue"; src: url("../../assets/fonts/subtitle-preview/BebasNeue-Regular.ttf") format("truetype"); font-weight: 700; }
@font-face { font-family: "Bricolage Grotesque"; src: url("../../assets/fonts/subtitle-preview/BricolageGrotesque.ttf") format("truetype"); font-weight: 100 900; }
@font-face { font-family: "League Spartan"; src: url("../../assets/fonts/subtitle-preview/LeagueSpartan.ttf") format("truetype"); font-weight: 100 900; }
@font-face { font-family: "Oswald"; src: url("../../assets/fonts/subtitle-preview/Oswald.ttf") format("truetype"); font-weight: 200 700; }
</style>
